import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { appError } from '../../lib/logger';

// What an integration screen shows when something fails - a crashed render
// (via the boundary below) or a failed load (used directly by the screens'
// own catch blocks) - instead of a blank page or a swallowed rejection. The
// message is the real one, with a retry, so a failing integration is
// something the user can read and act on.
export function IntegrationErrorPanel({
  title = 'This integration screen failed',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col gap-2 rounded-2xl border border-red-500/30 bg-red-500/[0.06] p-4">
      <div className="flex items-center gap-2 font-semibold text-red-600 dark:text-red-400">
        <AlertTriangle size={16} aria-hidden /> {title}
      </div>
      <p className="m-0 text-sm text-black/70 dark:text-white/70 break-words">{message}</p>
      {onRetry && (
        <div>
          <Button variant="bordered" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

interface BoundaryProps {
  children: ReactNode;
  // Changing this (the route) clears a previous error, so navigating away from
  // a broken screen never leaves the boundary stuck.
  resetKey: string;
}

export class IntegrationErrorBoundary extends Component<BoundaryProps, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    appError('[Integrations] screen crashed:', error, info.componentStack);
  }

  componentDidUpdate(prev: BoundaryProps) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    if (this.state.error) {
      return <IntegrationErrorPanel message={this.state.error.message || 'Unexpected error.'} onRetry={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

// Layout route wrapping every /integrations/* screen.
export function IntegrationRoutes() {
  const { pathname } = useLocation();
  return (
    <IntegrationErrorBoundary resetKey={pathname}>
      <Outlet />
    </IntegrationErrorBoundary>
  );
}
